import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import { sessions, users } from "./auth.schema";

const timestamp = (name: string) => integer(name, { mode: "timestamp" });
const bool = (name: string) => integer(name, { mode: "boolean" });

// Better Auth 1.7 MCP OAuth provider and JWT plugin tables. Field and table
// names follow the provider's generated SQLite schema verbatim.
export const jwks = sqliteTable("jwks", {
  id: text("id").primaryKey(),
  publicKey: text("publicKey").notNull(),
  privateKey: text("privateKey").notNull(),
  createdAt: timestamp("createdAt").notNull(),
  expiresAt: timestamp("expiresAt"),
  alg: text("alg"),
  crv: text("crv"),
});

export const oauthClient = sqliteTable("oauthClient", {
  id: text("id").primaryKey(),
  clientId: text("clientId").notNull().unique(),
  clientSecret: text("clientSecret"),
  clientDiscoveryId: text("clientDiscoveryId"),
  disabled: bool("disabled"),
  skipConsent: bool("skipConsent"),
  enableEndSession: bool("enableEndSession"),
  subjectType: text("subjectType"),
  scopes: text("scopes"),
  clientCredentialsScopes: text("clientCredentialsScopes"),
  userId: text("userId").references(() => users.id, { onDelete: "cascade" }),
  createdAt: timestamp("createdAt"),
  updatedAt: timestamp("updatedAt"),
  name: text("name"),
  uri: text("uri"),
  icon: text("icon"),
  contacts: text("contacts"),
  tos: text("tos"),
  policy: text("policy"),
  softwareId: text("softwareId"),
  softwareVersion: text("softwareVersion"),
  softwareStatement: text("softwareStatement"),
  redirectUris: text("redirectUris").notNull(),
  postLogoutRedirectUris: text("postLogoutRedirectUris"),
  backchannelLogoutUri: text("backchannelLogoutUri"),
  backchannelLogoutSessionRequired: bool("backchannelLogoutSessionRequired"),
  tokenEndpointAuthMethod: text("tokenEndpointAuthMethod"),
  applicationType: text("applicationType"),
  jwks: text("jwks"),
  jwksUri: text("jwksUri"),
  grantTypes: text("grantTypes"),
  responseTypes: text("responseTypes"),
  requirePKCE: bool("requirePKCE"),
  dpopBoundAccessTokens: bool("dpopBoundAccessTokens"),
  referenceId: text("referenceId"),
  metadata: text("metadata"),
}, (table) => [index("oauthClient_userId_idx").on(table.userId)]);

export const oauthResource = sqliteTable("oauthResource", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull().unique(),
  name: text("name").notNull(),
  accessTokenTtl: integer("accessTokenTtl"),
  refreshTokenTtl: integer("refreshTokenTtl"),
  signingAlgorithm: text("signingAlgorithm"),
  signingKeyId: text("signingKeyId"),
  allowedScopes: text("allowedScopes"),
  customClaims: text("customClaims"),
  dpopBoundAccessTokensRequired: bool("dpopBoundAccessTokensRequired"),
  disabled: bool("disabled"),
  createdAt: timestamp("createdAt"),
  updatedAt: timestamp("updatedAt"),
  policyVersion: integer("policyVersion"),
  metadata: text("metadata"),
});

export const oauthClientResource = sqliteTable("oauthClientResource", {
  id: text("id").primaryKey(),
  clientId: text("clientId").notNull().references(() => oauthClient.clientId, { onDelete: "cascade" }),
  resourceId: text("resourceId").notNull().references(() => oauthResource.identifier, { onDelete: "cascade" }),
  metadata: text("metadata"),
  createdAt: timestamp("createdAt"),
}, (table) => [
  index("oauthClientResource_clientId_idx").on(table.clientId),
  index("oauthClientResource_resourceId_idx").on(table.resourceId),
]);

export const oauthRefreshToken = sqliteTable("oauthRefreshToken", {
  id: text("id").primaryKey(),
  token: text("token").notNull().unique(),
  clientId: text("clientId").notNull().references(() => oauthClient.clientId, { onDelete: "cascade" }),
  sessionId: text("sessionId").references(() => sessions.id, { onDelete: "set null" }),
  userId: text("userId").notNull().references(() => users.id, { onDelete: "cascade" }),
  referenceId: text("referenceId"),
  authorizationCodeId: text("authorizationCodeId"),
  resources: text("resources"),
  requestedUserInfoClaims: text("requestedUserInfoClaims"),
  expiresAt: timestamp("expiresAt").notNull(),
  createdAt: timestamp("createdAt").notNull(),
  revoked: timestamp("revoked"),
  rotatedAt: timestamp("rotatedAt"),
  rotationReplayResponse: text("rotationReplayResponse"),
  rotationReplayExpiresAt: timestamp("rotationReplayExpiresAt"),
  authTime: timestamp("authTime"),
  confirmation: text("confirmation"),
  scopes: text("scopes").notNull(),
}, (table) => [
  index("oauthRefreshToken_clientId_idx").on(table.clientId),
  index("oauthRefreshToken_sessionId_idx").on(table.sessionId),
  index("oauthRefreshToken_userId_idx").on(table.userId),
  index("oauthRefreshToken_authorizationCodeId_idx").on(table.authorizationCodeId),
]);

export const oauthAccessToken = sqliteTable("oauthAccessToken", {
  id: text("id").primaryKey(),
  token: text("token").notNull().unique(),
  clientId: text("clientId").notNull().references(() => oauthClient.clientId, { onDelete: "cascade" }),
  sessionId: text("sessionId").references(() => sessions.id, { onDelete: "set null" }),
  userId: text("userId").references(() => users.id, { onDelete: "cascade" }),
  referenceId: text("referenceId"),
  authorizationCodeId: text("authorizationCodeId"),
  resources: text("resources"),
  requestedUserInfoClaims: text("requestedUserInfoClaims"),
  refreshId: text("refreshId").references(() => oauthRefreshToken.id, { onDelete: "cascade" }),
  expiresAt: timestamp("expiresAt").notNull(),
  createdAt: timestamp("createdAt").notNull(),
  revoked: timestamp("revoked"),
  confirmation: text("confirmation"),
  scopes: text("scopes").notNull(),
}, (table) => [
  index("oauthAccessToken_clientId_idx").on(table.clientId),
  index("oauthAccessToken_sessionId_idx").on(table.sessionId),
  index("oauthAccessToken_userId_idx").on(table.userId),
  index("oauthAccessToken_authorizationCodeId_idx").on(table.authorizationCodeId),
  index("oauthAccessToken_refreshId_idx").on(table.refreshId),
]);

export const oauthConsent = sqliteTable("oauthConsent", {
  id: text("id").primaryKey(),
  clientId: text("clientId").notNull().references(() => oauthClient.clientId, { onDelete: "cascade" }),
  userId: text("userId").notNull().references(() => users.id, { onDelete: "cascade" }),
  referenceId: text("referenceId"),
  resources: text("resources"),
  requestedUserInfoClaims: text("requestedUserInfoClaims"),
  scopes: text("scopes").notNull(),
  createdAt: timestamp("createdAt").notNull(),
  updatedAt: timestamp("updatedAt").notNull(),
}, (table) => [
  index("oauthConsent_clientId_idx").on(table.clientId),
  index("oauthConsent_userId_idx").on(table.userId),
]);

export const oauthClientAssertion = sqliteTable("oauthClientAssertion", {
  id: text("id").primaryKey(),
  expiresAt: timestamp("expiresAt").notNull(),
});
